import * as Sentry from "@sentry/react";
import environment from "@/modules/environment";
import { getLocale } from "@/utils";

const release = `${environment.appName}@${__APP_VERSION__}`;

function getIsDev(functionName: string) {
  if (isDev) {
    log.info(`Sentry: ignoring ${functionName} call in development`);
    return true;
  }
  return false;
}

function init() {
  if (getIsDev("init")) return;
  Sentry.init({
    dsn: environment.sentryDsn,
    environment: __IS_PRERELEASE__ ? "pre-production" : environment.envMode,
    debug: isDev,
    release,
    beforeBreadcrumb(breadcrumb, hint) {
      (hint?.event?.breadcrumbs as any[])?.forEach((b, i) => {
        if (b.data?.url?.includes("data:application")) b.data.url = "Removed, included data url";
      });
      return breadcrumb;
    }
  });
  Sentry.setContext("system", {
    browser: environment.system.getBrowserName(),
    os: environment.system.getOSName(),
    mode: environment.appMode,
    locale: getLocale()
  });
}

function sendMessage(message: string) {
  if (getIsDev("sendMessage")) return;
  Sentry.captureMessage(message);
}

async function sendRestart(cause: string, restartInstance: number) {
  if (getIsDev("sendRestart")) return;
  Sentry.withScope((scope) => {
    scope.setLevel("warning");
    scope.setTag("restart-cause", cause);
    scope.setExtra("restartInstance", restartInstance);
    Sentry.captureMessage(`App restart: ${cause}`);
  });
  // Make sure the event is delivered before the page reloads
  await Sentry.flush(2000);
}

function sendException(exception: any, hint?: { key: string; extra: any }) {
  if (getIsDev("sendException")) return;
  Sentry.withScope((scope) => {
    if (hint) scope.setExtra(hint.key, hint.extra);
    Sentry.captureException(exception);
  });
}

function setUser(user: User | null) {
  if (getIsDev("setUser")) return;
  Sentry.setUser(
    user
      ? {
          id: user.objectId,
          email: user.email
        }
      : null
  );
}

const CrashReporter = {
  init,
  sendMessage,
  sendRestart,
  sendException,
  setUser
};

export default CrashReporter;
